import { c as _c } from "react/compiler-runtime";
import { saveConsents, updateServicesConsents, useHasServiceConsent } from '@s-group/react-usercentrics';
import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import { useCallback } from 'react';
import { NoSSR } from '#cutils/misc/NoSSR';
import { IconSystem } from '#icons/IconSystem';
import styles from './PrivacyEmbedConsent.module.scss';
import { TextButton } from './base/TextButton';

/**
 * Returns the corresponding PrivacyIcon for the given service.
 *
 * @param service The embed service name
 * @return The icon an title corresponding to the embed service.
 */
function getPrivacyIcon(service: string) {
  let title: string;
  let icon: ComponentProps<typeof IconSystem>['icon'];
  switch (service) {
    case 'facebook':
      title = 'Facebook';
      icon = 'facebook';
      break;
    case 'googlemaps':
      title = 'Google Maps';
      icon = 'google-maps';
      break;
    case 'twitter':
      title = 'X (ehemals Twitter)';
      icon = 'twitter-x';
      break;
    case 'youtube':
      title = 'YouTube';
      icon = 'youtube';
      break;
    case 'tiktok':
      title = 'TikTok';
      icon = 'tiktok';
      break;
    case 'instagram':
      title = 'Instagram';
      icon = 'instagram';
      break;
    case 'br':
    default:
      title = 'BR';
      icon = 'br';
      break;
  }
  return {
    title,
    icon
  };
}
type Props = {
  children: ReactNode;
  service: string;
  text?: string | null;
  title: string;
  cmpId?: string | null;
  className?: string;
};
type PropsRenderer = Omit<Props, 'cmpId' | 'text'> & {
  cmpId: NonNullable<Props['cmpId']>;
  text: NonNullable<Props['text']>;
};
function PrivacyEmbedConsentWithCmp(t0) {
  const $ = _c(12);
  const {
    children,
    service,
    text,
    title,
    cmpId,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const consentGiven = useHasServiceConsent(cmpId);
  let t2;
  if ($[0] !== cmpId) {
    t2 = () => {
      updateServicesConsents([{
        id: cmpId,
        consent: true
      }]);
      saveConsents();
    };
    $[0] = cmpId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const onAcceptService = t2;
  let t3;
  if ($[2] !== children || $[3] !== className || $[4] !== consentGiven || $[5] !== onAcceptService || $[6] !== service || $[7] !== text || $[8] !== title) {
    t3 = consentGiven ? children : <section className={`${className} ${styles.wrapper}`} data-theme-area="dark"><div className={styles.header}><IconSystem {...getPrivacyIcon(service)} variant="filled" size="xl" /><span className="heading3">{title}</span></div>{text && <p className="body1">{text}</p>}<TextButton display="highlight" className={styles.button} onClick={onAcceptService}>Inhalte anzeigen</TextButton></section>;
    $[2] = children;
    $[3] = className;
    $[4] = consentGiven;
    $[5] = onAcceptService;
    $[6] = service;
    $[7] = text;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <>{t3}</>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
export function PrivacyEmbedConsent(t0) {
  const $ = _c(9);
  const {
    children,
    service,
    text,
    title,
    cmpId,
    className
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== className || $[2] !== cmpId || $[3] !== service || $[4] !== text || $[5] !== title) {
    t1 = cmpId && text ? <PrivacyEmbedConsentWithCmp service={service} text={text} title={title} cmpId={cmpId} className={className}>{children}</PrivacyEmbedConsentWithCmp> : children;
    $[0] = children;
    $[1] = className;
    $[2] = cmpId;
    $[3] = service;
    $[4] = text;
    $[5] = title;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <NoSSR>{t1}</NoSSR>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}